/**
 * EmbroideryInventoryModal — inline inventory editor for the Embroidery Studio.
 * Uses the same POST /api/admin/inventory/adjust endpoint as SuperAdmin, so all
 * corrections write to the immutable ledger. Reason is required; whole ints only;
 * conflicts against active reservations require an explicit second confirmation.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { X, AlertTriangle, ArrowLeft } from "lucide-react";

export default function EmbroideryInventoryModal({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { row } | null
  const [newQty, setNewQty] = useState("");
  const [reason, setReason] = useState("");
  const [confirmConflict, setConfirmConflict] = useState(null); // { active_reservations, message } | null
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/inventory");
      setRows(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const grouped = useMemo(() => {
    const map = {};
    rows.forEach((r) => { (map[r.item_type] = map[r.item_type] || []).push(r); });
    return map;
  }, [rows]);

  const startEdit = (row) => {
    setEditing({ row });
    setNewQty(String(row.remaining_qty));
    setReason("");
    setConfirmConflict(null);
  };

  const cancelEdit = () => {
    setEditing(null);
    setNewQty("");
    setReason("");
    setConfirmConflict(null);
  };

  const submit = async ({ force } = {}) => {
    if (!editing) return;
    const row = editing.row;
    const trimmed = String(newQty).trim();
    if (!/^\d+$/.test(trimmed)) {
      toast.error("Enter a whole number (no decimals or negatives).");
      return;
    }
    const val = parseInt(trimmed, 10);
    if (val < 0) {
      toast.error("Quantity cannot be negative.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Reason is required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/inventory/adjust", {
        item_type: row.item_type,
        color: row.color,
        new_remaining: val,
        reason: reason.trim(),
        force: !!force,
      });
      toast.success(`Updated ${row.item_type} · ${row.color} → ${data.row.remaining_qty}`);
      cancelEdit();
      await load();
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && detail.conflict === "new_remaining_below_active_reservations") {
        setConfirmConflict({
          active_reservations: detail.active_reservations,
          message: detail.message,
        });
      } else {
        toast.error(formatErr(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6"
      onClick={onClose}
      data-testid="embroidery-inventory-modal"
      role="dialog"
      aria-label="Edit Inventory"
    >
      <div
        className="relative bg-motif rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 p-4 border-b border-motif">
          {editing && (
            <button
              onClick={cancelEdit}
              className="btn-ghost text-xs flex items-center gap-1"
              data-testid="embroidery-inventory-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          )}
          <div className="font-display text-lg text-motif flex-1">
            {editing ? `Adjust ${editing.row.item_type} · ${editing.row.color}` : "Edit Inventory"}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-accent-motif hover:opacity-80 flex items-center justify-center"
            aria-label="Close"
            data-testid="embroidery-inventory-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading && <div className="text-motif-muted text-sm text-center py-8">Loading…</div>}

          {!loading && !editing && (
            <div className="space-y-5">
              <AddInventoryForm itemTypes={Object.keys(grouped)} onAdded={() => { load(); onChanged && onChanged(); }} />
              {Object.keys(grouped).map((type) => (
                <div key={type}>
                  <div className="text-[10px] tracking-luxe text-primary-motif mb-2">
                    {type} · {grouped[type].length} colors
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {grouped[type].map((r) => {
                      const out = r.remaining_qty <= 0;
                      const low = !out && r.remaining_qty <= 2;
                      return (
                        <button
                          key={r.item_type + r.color}
                          onClick={() => startEdit(r)}
                          className="flex items-center gap-3 p-2.5 rounded-xl border text-left transition hover:opacity-90"
                          style={{
                            borderColor: out ? "#ef4444" : low ? "#f59e0b" : "var(--motif-border)",
                            background: out ? "#ef44441a" : low ? "#f59e0b1a" : "var(--motif-accent)",
                          }}
                          data-testid={`inv-edit-${type.toLowerCase()}-${r.color.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <div
                            className="w-9 h-9 rounded-md border flex-shrink-0"
                            style={{ background: r.swatch_hex || "#CCC", borderColor: "var(--motif-border)" }}
                            title={r.color}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-motif truncate">{r.color}</div>
                            <div className="text-[10px] text-motif-muted">
                              {out ? "Out of stock" : low ? "Low stock" : "Available"}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-display text-xl text-primary-motif tabular-nums">{r.remaining_qty}</div>
                            <div className="text-[9px] tracking-luxe text-motif-muted">
                              of {(r.starting_qty ?? r.remaining_qty)}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && editing && (
            <EditForm
              row={editing.row}
              newQty={newQty}
              setNewQty={setNewQty}
              reason={reason}
              setReason={setReason}
              saving={saving}
              onCancel={cancelEdit}
              onSubmit={() => submit({})}
              onConfirmForce={() => submit({ force: true })}
              confirmConflict={confirmConflict}
              clearConflict={() => setConfirmConflict(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function EditForm({ row, newQty, setNewQty, reason, setReason, saving, onCancel, onSubmit, onConfirmForce, confirmConflict, clearConflict }) {
  const currentQty = row.remaining_qty;
  const parsed = /^\d+$/.test(String(newQty).trim()) ? parseInt(newQty, 10) : null;
  const diff = parsed != null ? parsed - currentQty : null;
  const reserved = row.reserved_qty || 0;

  return (
    <div className="space-y-4" data-testid="embroidery-inventory-edit-form">
      <div className="flex items-center gap-3">
        <div
          className="w-14 h-14 rounded-lg border"
          style={{ background: row.swatch_hex || "#CCC", borderColor: "var(--motif-border)" }}
        />
        <div>
          <div className="text-[10px] tracking-luxe text-motif-muted">{row.item_type}</div>
          <div className="font-display text-lg text-motif">{row.color}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] tracking-luxe text-motif-muted">CURRENT</div>
          <div className="font-display text-2xl text-motif tabular-nums">{currentQty}</div>
        </div>
        <div>
          <label className="text-[10px] tracking-luxe text-motif-muted block" htmlFor="inv-new-qty">NEW REMAINING</label>
          <input
            id="inv-new-qty"
            inputMode="numeric"
            pattern="[0-9]*"
            className="input-motif tabular-nums text-lg"
            value={newQty}
            onChange={(e) => setNewQty(e.target.value.replace(/[^0-9]/g, ""))}
            autoFocus
            data-testid="embroidery-inventory-new-qty"
          />
        </div>
        <div>
          <div className="text-[10px] tracking-luxe text-motif-muted">DIFFERENCE</div>
          <div
            className="font-display text-2xl tabular-nums"
            style={{ color: diff == null ? "var(--motif-text-muted)" : diff < 0 ? "#ef4444" : diff > 0 ? "#10b981" : "var(--motif-text-muted)" }}
            data-testid="embroidery-inventory-diff"
          >
            {diff == null ? "—" : diff > 0 ? `+${diff}` : diff}
          </div>
        </div>
      </div>

      {reserved > 0 && (
        <div className="text-[11px] text-motif-muted flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" style={{ color: "#f59e0b" }} />
          {reserved} active reservation{reserved === 1 ? "" : "s"} — new value must be at least {reserved} unless you explicitly override.
        </div>
      )}

      <div>
        <label className="text-[10px] tracking-luxe text-motif-muted block mb-1" htmlFor="inv-reason">
          REASON <span className="text-primary-motif">*</span>
        </label>
        <input
          id="inv-reason"
          className="input-motif"
          placeholder="e.g. Vendor delivered 4 more Army Green"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="embroidery-inventory-reason"
        />
      </div>

      {confirmConflict && (
        <div className="p-3 rounded-lg border" style={{ borderColor: "#ef4444", background: "#ef44441a" }} data-testid="embroidery-inventory-conflict">
          <div className="flex items-center gap-2 text-sm text-motif mb-2">
            <AlertTriangle className="w-4 h-4" style={{ color: "#ef4444" }} />
            <span className="font-medium">Below active reservations</span>
          </div>
          <p className="text-xs text-motif-muted mb-3">{confirmConflict.message}</p>
          <div className="flex gap-2">
            <button onClick={clearConflict} className="btn-ghost text-xs flex-1" data-testid="embroidery-inventory-conflict-cancel">Cancel</button>
            <button onClick={onConfirmForce} disabled={saving} className="btn-primary text-xs flex-1" data-testid="embroidery-inventory-conflict-confirm">
              {saving ? "Saving…" : "Confirm override"}
            </button>
          </div>
        </div>
      )}

      {!confirmConflict && (
        <div className="flex gap-2 pt-2">
          <button onClick={onCancel} className="btn-ghost text-xs flex-1" data-testid="embroidery-inventory-cancel">Cancel</button>
          <button
            onClick={onSubmit}
            disabled={saving || diff === 0 || !reason.trim() || parsed == null}
            className="btn-primary text-xs flex-1"
            data-testid="embroidery-inventory-confirm"
          >
            {saving ? "Saving…" : "Confirm adjustment"}
          </button>
        </div>
      )}
    </div>
  );
}

// Phase 5E: create a new inventory item or add a color to an existing item.
function AddInventoryForm({ itemTypes, onAdded }) {
  const [open, setOpen] = useState(false);
  const [itemType, setItemType] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState("");
  const [swatch, setSwatch] = useState("#CCCCCC");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const q = parseInt(qty, 10);
    if (!itemType.trim() || !color.trim()) return toast.error("Item and color are required");
    if (!Number.isInteger(q) || q < 0) return toast.error("Quantity must be a whole number");
    setSaving(true);
    try {
      await api.post("/admin/inventory/add", { item_type: itemType.trim(), color: color.trim(), qty: q, swatch_hex: swatch });
      toast.success(`${itemType.trim()} · ${color.trim()} added (${q})`);
      setItemType(""); setColor(""); setQty(""); setOpen(false);
      onAdded && onAdded();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-primary text-xs px-4 py-2" data-testid="inventory-add-open">
        + Add item / color
      </button>
    );
  }
  return (
    <div className="p-3 rounded-xl border border-motif bg-accent-motif/40" data-testid="inventory-add-form">
      <div className="text-[10px] tracking-luxe text-primary-motif mb-2">New inventory item / color</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <input list="inventory-item-types" className="input-motif text-sm w-full" placeholder="Item (e.g. Turban)"
            value={itemType} onChange={(e) => setItemType(e.target.value)} data-testid="inventory-add-item" />
          <datalist id="inventory-item-types">
            {itemTypes.map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
        <input className="input-motif text-sm" placeholder="Color (e.g. Gold)" value={color}
          onChange={(e) => setColor(e.target.value)} data-testid="inventory-add-color" />
        <input className="input-motif text-sm" type="number" min="0" placeholder="Quantity" value={qty}
          onChange={(e) => setQty(e.target.value)} data-testid="inventory-add-qty" />
        <label className="flex items-center gap-2 text-xs text-motif-muted">
          Swatch
          <input type="color" value={swatch} onChange={(e) => setSwatch(e.target.value)} data-testid="inventory-add-swatch" />
        </label>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setOpen(false)} className="btn-ghost text-xs flex-1">Cancel</button>
        <button onClick={save} disabled={saving} className="btn-primary text-xs flex-1" data-testid="inventory-add-save">
          {saving ? "Saving…" : "Add"}
        </button>
      </div>
    </div>
  );
}
